import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { AppointmentStatus, TimeInterval } from '@geely/shared';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string): string {
  if (!date) return 'Data inválida';
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

export function formatTime(time: string): string {
  return time;
}

export function getDayOfWeekLabel(day: string): string {
  if (!day) return '-';
  const dateObj = new Date(day + 'T00:00:00');
  const dayOfWeek = dateObj.toLocaleDateString('pt-BR', { weekday: 'long' });
  return dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
}

export function getStatusLabel(status: AppointmentStatus): string {
  const labels: Record<AppointmentStatus, string> = {
    pending: 'Pendente',
    in_progress: 'Em andamento',
    completed: 'Concluído',
    cancelled: 'Cancelado',
    customer_absent: 'Cliente ausente',
  };
  return labels[status];
}

export function getStatusColor(status: AppointmentStatus): string {
  const colors: Record<AppointmentStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
    completed: 'bg-green-100 text-green-800 border-green-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300',
    customer_absent: 'bg-gray-100 text-gray-800 border-gray-300',
  };
  return colors[status];
}

export function generateTimeSlots(startTime: string, endTime: string, interval: number): string[] {
  const slots: string[] = [];
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  let currentMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  while (currentMinutes < endMinutes) {
    const hours = Math.floor(currentMinutes / 60);
    const minutes = currentMinutes % 60;
    slots.push(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
    currentMinutes += interval;
  }

  return slots;
}

export function getAvailableDates(daysAhead?: number): string[] {
  const days = daysAhead ?? (Number(import.meta.env.VITE_AVAILABLE_DAYS_AHEAD) || 60);
  const dates: string[] = [];
  const today = new Date();

  for (let i = 1; i <= days; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const dateString = date.toISOString().split('T')[0];
    dates.push(dateString);
  }

  return dates;
}

export function getDayOfWeekFromDate(dateString: string): number {
  const date = new Date(dateString + 'T00:00:00');
  return date.getDay();
}

/**
 * Extrai campos de horário não-vazios do formulário de horários de funcionamento.
 * Campos com valor falsy (string vazia, undefined, null, 0) são omitidos,
 * garantindo que a API receba apenas campos com valor válido.
 */
export function extractTimeFields(data: {
  startTime?: string;
  endTime?: string;
  timeInterval?: number;
  lunchStartTime?: string;
  lunchEndTime?: string;
}) {
  return {
    ...(data.startTime ? { startTime: data.startTime } : {}),
    ...(data.endTime ? { endTime: data.endTime } : {}),
    ...(data.timeInterval ? { timeInterval: data.timeInterval as TimeInterval } : {}),
    ...(data.lunchStartTime ? { lunchStartTime: data.lunchStartTime } : {}),
    ...(data.lunchEndTime ? { lunchEndTime: data.lunchEndTime } : {}),
  };
}

export function formatDateForSelect(date: string): string {
  if (!date) return 'Data inválida';
  const [year, month, day] = date.split('-');
  const dateObj = new Date(date + 'T00:00:00');
  const dayOfWeek = dateObj.toLocaleDateString('pt-BR', { weekday: 'long' });
  const capitalizedDayOfWeek = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
  return `${capitalizedDayOfWeek}, ${day}/${month}/${year}`;
}
